// third-party dependencies
import { Signal } from '@angular/core';
import type {
  AuthSession,
  ConfirmResetPasswordInput,
  ConfirmSignInInput,
  ConfirmSignInOutput,
  ConfirmSignUpInput,
  ConfirmSignUpOutput,
  ConfirmUserAttributeInput,
  CurrentUser,
  FetchAuthSessionOptions,
  FetchMFAPreferenceOutput,
  ResendSignUpCodeInput,
  ResendSignUpCodeOutput,
  ResetPasswordInput,
  ResetPasswordOutput,
  SendUserAttributeVerificationCodeInput,
  SendUserAttributeVerificationCodeOutput,
  SignInInput,
  SignInOutput,
  SignInWithMagicLinkInput,
  SignInWithRedirectInput,
  SignUpInput,
  SignUpOutput,
  UpdatePasswordInput,
  UpdateUserAttributeInput,
  UpdateUserAttributeOutput,
} from '@fantaz/shared/data-access/models';
import {
  SetUpTOTPOutput,
  UpdateMFAPreferenceInput,
  VerifyTOTPSetupInput,
} from 'aws-amplify/auth';

import { Observable } from 'rxjs';

// *****************************************************************************
// *** Types and interfaces
// *****************************************************************************
export type CheckUsernameOptions = {
  username: Username;
};

export type CheckUsernameResult = {
  isValid: boolean;
};

export type ConfirmSignUpOptions = {
  code: string;
  username: string;
};

export type SignInData = {
  username: string;
  password: string;
};

export enum UsernameType {
  email = 'email',
  phone = 'phone',
  phoneNumber = 'phone_number',
  username = 'username',
}

export enum ExternalProvider {
  Google = 'Google',
  Facebook = 'Facebook',
}

export enum MfaMethod {
  topP = 'TOTP',
  sms = 'SMS',
  nonMfa = 'NOMFA',
  smsMfa = 'SMS_MFA',
  softwareTokenMfa = 'SOFTWARE_TOKEN_MFA',
}

export class Username {
  value: string;

  constructor(value: string) {
    this.value = value;
  }

  get type(): UsernameType {
    const isUsPhone = this.value.match(
      /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})$/,
    );
    const isEmail = this.value.includes('@');

    if (isUsPhone) {
      return UsernameType.phone;
    }

    if (isEmail) {
      return UsernameType.email;
    }

    return UsernameType.username;
  }

  get isEmail(): boolean {
    return this.type === UsernameType.email;
  }

  get isPhone(): boolean {
    return this.type === UsernameType.phone;
  }

  toString() {
    return this.value;
  }
}

export type SignUpResponse = {
  CodeDeliveryDetails: {
    AttributeName: 'email' | string;
    DeliveryMedium: 'EMAIL' | string;
    Destination: string;
  };
  UserConfirmed: boolean;
  UserSub: string;
};

export interface AuthService {
  saveIntoLocalStorage(session: AuthSession): void;

  getUserId(): string;

  getUserIdToken(): string;

  getUserName(): string;

  onAuthStateChanged(callback: any): any;

  /**
   * Signs a user in
   *
   * @param input -  The SignInOption object
   * @returns SignInOutput
   * @throws service: {@link InitiateAuthException }, {@link RespondToAuthChallengeException }
   *  - Cognito service errors thrown during the sign-in process.
   * @throws validation: {@link AuthValidationErrorCode  } - Validation errors thrown when either username or password
   *  are not defined.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  signIn(data: SignInInput): Observable<SignInOutput>;

  /**
   * Checks if the user can sign in with the provider.
   *
   * @param {string} provider -  The provider to check
   * @returns boolean
   */
  canISignIn(provider: string): Observable<boolean>;

  sendMagicLink(data: SignInInput): Observable<ConfirmSignInOutput>;

  singInWithMagicLink(data: SignInWithMagicLinkInput): Observable<SignInOutput>;

  /**
   * Fetches the preferred MFA setting and enabled MFA settings for the user.
   *
   * @returns FetchMFAPreferenceOutput
   * @throws  -{@link GetUserException} : error thrown when the service fails to fetch MFA preference
   * and settings.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  fetchMFAPreference(): Observable<FetchMFAPreferenceOutput>;

  /**
   * Continues or completes the sign in process when required by the initial call to `signIn`.
   *
   * @param input -  The ConfirmSignInInput object
   * @returns ConfirmSignInOutput
   * @throws  -{@link VerifySoftwareTokenException }:
   * Thrown due to an invalid MFA token.
   * @throws  -{@link RespondToAuthChallengeException }:
   * Thrown due to an invalid auth challenge response.
   * @throws  -{@link AssociateSoftwareTokenException}:
   * Thrown due to a service error during the MFA setup process.
   * @throws  -{@link AuthValidationErrorCode }:
   * Thrown when `challengeResponse` is not defined.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  confirmSignIn(input: ConfirmSignInInput): Observable<ConfirmSignInOutput>;

  /**
   * Sign in a user with external provider.
   * @param provider {ExternalProvider} - external provider to sign in with
   * @param options {T} - options for external provider
   */
  signInWithExternalProvider<T>(
    data: SignInWithRedirectInput,
  ): Observable<boolean>;

  /**
   * Sign up a user.
   *
   * @param data {SignUpInput} - object with user properties to sign up
   * @returns Observable<SignUpOutput> - result of sign up
   */
  signUp(data: SignUpInput): Observable<SignUpOutput>;

  /**
   * Confirm sign up.
   * @param data {CheckUsernameOptions} - object with username property
   * @returns Observable<boolean> - result of sign up confirmation
   */
  confirmSignUp(data: ConfirmSignUpInput): Observable<ConfirmSignUpOutput>;

  /**
   * Resend the confirmation code while signing up
   *
   * @param input -  The ResendSignUpCodeInput object
   * @returns ResendSignUpCodeOutput
   * @throws service: {@link ResendConfirmationException } - Cognito service errors thrown when resending the code.
   * @throws validation: {@link AuthValidationErrorCode } - Validation errors thrown either username are not defined.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  resendSignUpCode(
    data: ResendSignUpCodeInput,
  ): Observable<ResendSignUpCodeOutput>;

  /**
   * Marks device as remembered while authenticated.
   *
   * @throws - {@link UpdateDeviceStatusException} - Auth service errors thrown when
   * setting device status to remembered using an invalid device key.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  rememberDevice(): Observable<void>;

  /**
   * Check if user is logged in.
   * @returns {Observable<boolean>} - true if user is logged in, false otherwise
   */
  isLoggedIn(): Observable<boolean>;

  /**
   * Check if user is authenticated.
   * @returns {Signal<boolean>} - signal that emits true if user is authenticated, false otherwise
   */
  isUserLoggedIn(): Signal<boolean>;

  /**
   * Gets the authentication session.
   * @returns {Observable<AuthSession>} - An observable that emits the authentication session.
   */
  getAuthSession(data?: FetchAuthSessionOptions): Observable<AuthSession>;

  /**
   * Gets the current user from de api
   *
   * @param input -  The GetCurrentUserInput object.
   * @returns GetCurrentUserOutput
   * @throws - {@link InitiateAuthException} - Thrown when the service fails to refresh the tokens.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  getCurrentUser(): Observable<CurrentUser>;

  /**
   * Refreshes the current session.
   *
   */
  refreshSession(): Observable<AuthSession>;

  /**
   * Resets a user's password.
   *
   * @param input -  The ResetPasswordInput object.
   * @returns ResetPasswordOutput
   * @throws -{@link ForgotPasswordException }
   * Thrown due to an invalid confirmation code or password.
   * @throws -{@link AuthValidationErrorCode }
   * Thrown due to an empty username.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   **/
  resetPassword(data: ResetPasswordInput): Observable<ResetPasswordOutput>;

  confirmResetPassword(data: ConfirmResetPasswordInput): Observable<void>;

  /**
   * Updates user's password while authenticated.
   *
   * @param input - The UpdatePasswordInput object.
   * @throws - {@link ChangePasswordException} - Cognito service errors thrown when updating a password.
   * @throws - {@link AuthValidationErrorCode} - Validation errors thrown when oldPassword or newPassword are empty.
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  updatePassword(data: UpdatePasswordInput): Observable<void>;

  /**
   * Signs a user out
   *
   * @param input - The SignOutInput object
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  signOut(global?: boolean): Observable<boolean>;

  /**
   * Updates user's attribute while authenticated.
   *
   * @param input - The UpdateUserAttributeInput object
   * @returns UpdateUserAttributeOutput
   * @throws - {@link UpdateUserAttributesException}
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  updateUserAttribute(
    data: UpdateUserAttributeInput,
    opts?: { custom?: boolean },
  ): Observable<UpdateUserAttributeOutput>;

  /**
   * Resend user's confirmation code when updating attributes while authenticated.
   *
   * @param input - The SendUserAttributeVerificationCodeInput object
   * @returns SendUserAttributeVerificationCodeOutput
   * @throws - {@link GetUserAttributeVerificationException}
   * @throws AuthTokenConfigException - Thrown when the token provider config is invalid.
   */
  sendUserAttributeVerificationCode(
    data: SendUserAttributeVerificationCodeInput,
  ): Observable<SendUserAttributeVerificationCodeOutput>;

  /**
   * Verify contact point of the authenticated user.
   *
   * @param data {object} - object with attribute and value
   * @param data.attr {string} - attribute to verify (email or phone_number)
   * @param data.value {string} - value of attribute
   */
  confirmUserAttribute(
    data: ConfirmUserAttributeInput,
    opts?: { custom: boolean },
  ): Observable<void>;

  /**
   * Fetch authenticated user devices.
   *
   @returns {Observable<IAuthDevice[]>} - list of devices
   */
  getUserDevices(): Observable<any[]>;

  updateMFAPreference(arg: UpdateMFAPreferenceInput): Observable<void>;

  setupTOTP(): Observable<SetUpTOTPOutput>;

  verifyTOTPSetup(args: VerifyTOTPSetupInput): Observable<void>;

  currentAuthenticatedUser(): Observable<any>;

  /**
   * Check if username exists or is available.
   *
   * @param data {CheckUsernameOptions} - object with username property
   * @param data.username {Username} - username to check
   * @returns {Observable<CheckUsernameResult>} - result of username check
   */
  checkUsername(data: {
    attr: string;
    value: string;
  }): Observable<CheckUsernameResult>;

  clearAuthState(): void;

  redirectToLogin(): void;

  mfaNotification(
    deviceKey: string,
    mfaPreference: UpdateMFAPreferenceInput,
  ): Observable<{ message: string }>;

  getAuthenticationToken(): Observable<string | undefined>;

  getAuthenticationTokenFromSession(): Observable<string>;
}
